import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { onQuotaExceeded } from '../../utils/subscriptionModals'
import { trackQuotaExceeded } from '../../utils/analytics'
import { useAccount } from '../../hooks/useAccount'
import './QuotaExceededModal.css'

/**
 * Modal displayed when user exceeds their quota limit
 * Listens to global quota-exceeded events from API interceptor
 *
 * Phase 3: Now shows account context in error message (P2 enhancement)
 */
const QuotaExceededModal = () => {
  const { t } = useTranslation('subscription')
  const [isOpen, setIsOpen] = useState(false)
  const [data, setData] = useState(null)
  const navigate = useNavigate()

  // Phase 3: Get current account context for better error messaging
  const { selectedAccount } = useAccount()

  useEffect(() => {
    // Subscribe to quota exceeded events
    const unsubscribe = onQuotaExceeded((modalData) => {
      setData(modalData)
      setIsOpen(true)
      // Track quota exceeded event
      trackQuotaExceeded(modalData.quota_type)
    })

    // Cleanup on unmount
    return unsubscribe
  }, [])

  if (!isOpen || !data) return null

  const handleUpgrade = () => {
    setIsOpen(false)
    navigate('/subscription')
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  // Format quota type for display
  const quotaTypeLabel = data.quota_type === 'stories' ? t('quotaExceeded.quotaTypes.stories') : t('quotaExceeded.quotaTypes.regenerations')
  const quotaIcon = data.quota_type === 'stories' ? t('quotaExceeded.quotaTypes.icons.stories') : t('quotaExceeded.quotaTypes.icons.regenerations')

  // Calculate percentage for progress bar
  const percentage = data.unlimited ? 0 : Math.min((data.used / data.limit) * 100, 100)

  // Format reset date
  const resetDate = data.resets_at
    ? new Date(data.resets_at).toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : t('quotaExceeded.resetDateFallback')

  return (
    <div className="modal-overlay" onClick={handleClose} role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div className="modal-content quota-exceeded-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 id="modal-title">{t('quotaExceeded.title')}</h2>
          <button
            className="close-button"
            onClick={handleClose}
            aria-label={t('quotaExceeded.closeAriaLabel')}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="quota-icon">{quotaIcon}</div>

          {/* Phase 3: Show account name if available for better context */}
          {selectedAccount && (
            <p className="text-sm text-gray-600 mb-2">
              Account: <span className="font-semibold">@{selectedAccount.instagram_username || selectedAccount.username}</span>
            </p>
          )}

          <p className="quota-message">
            {t('quotaExceeded.message', { limit: data.limit, quotaType: quotaTypeLabel.toLowerCase() })}
          </p>

          {/* Progress bar */}
          <div className="quota-progress-container" role="progressbar" aria-valuenow={data.used} aria-valuemin="0" aria-valuemax={data.limit}>
            <div
              className={`quota-progress-bar ${percentage >= 100 ? 'exceeded' : ''}`}
              style={{ width: `${percentage}%` }}
            />
          </div>
          <p className="quota-numbers">
            <span className="used">{data.used}</span>
            <span className="separator">/</span>
            <span className="limit">{data.limit}</span>
          </p>

          {data.resets_at && (
            <p className="quota-reset">
              {t('quotaExceeded.resetDate', { date: resetDate })}
            </p>
          )}

          <div className="quota-info-box">
            <p>
              {t('quotaExceeded.upgradeMessage', { quotaType: quotaTypeLabel.toLowerCase() })}
            </p>
          </div>
        </div>

        <div className="modal-footer">
          <button className="button button-secondary" onClick={handleClose}>
            {t('quotaExceeded.buttons.close')}
          </button>
          <button className="button button-primary" onClick={handleUpgrade}>
            {t('quotaExceeded.buttons.upgrade')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default QuotaExceededModal
